/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {Checkbox, Popover} from "antd"
import {Icon} from "components/Icon"
import {BasePopup} from "components/popup"
import {BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton} from "components/buttons"
import styles from "./DailyAttendancePopup.module.css"
import {attendanceService, userServiceV3} from "services"
import {clone, cloneDeep, get, isEmpty, uniqBy} from "lodash"
import {
    convertTZ,
    formatDateOnly,
    getFieldLabel,
    getUserPhotoUrl,
    handleError,
    toastError,
    toastSuccess,
    toastWarning
} from "helpers"
import {validateAttendanceEvents, buildEventErrorMap} from "helpers/attendanceValidation"
import moment from "moment-timezone"
import {BaseLoading} from "components"
import {useAttendanceRounding, useCurrentProfilePermissions, useModel, useVisible} from "hooks"
import {
    AttendanceEvent,
    AttendanceEventType,
    AttendanceStatus,
    AttendanceType,
    BreakEventType,
    GeoLocationInfo
} from "types/attendance"
import cx from "classnames"
import {BaseTimePicker} from "components/DateTimePicker"
import {v4 as uuid} from "uuid"
import {GeoLocationMaps} from "components/GeoLocationMaps"
import {useAttendanceCheckBreakEventSetting} from "hooks/useAttendanceCheckBreakEventSetting"
import {ConfirmPopup} from "uiKit"
import {useMutation} from "@tanstack/react-query"
import {YesNo} from "types/common"
import {getDailyAttendancePermissions} from "helpers/attendance"
import {Modules, useEdularModulesContext} from "@edular/modules"
import {AttendanceRoundingType} from "types/settings/general-setting"
import {Settings} from "types/settings"
import {IrregularAttendanceBanner} from "../IrregularAttendanceBanner"

const EMPTY_VALUE = "-"

const EVENT_OPTIONS: Array<{name: string; value: AttendanceEventType}> = [
    {name: "Checkin", value: AttendanceEventType.Checkin},
    {name: "Checkout", value: AttendanceEventType.Checkout},
    {name: "In", value: AttendanceEventType.BreakEnd},
    {name: "Out", value: AttendanceEventType.BreakStart}
]

type Props = {
    isShow: boolean
    studentProfileIds?: number[]
    selectedAttendance?: any
    isExternshipAttendance?: boolean
    getListAttendances?: () => void
    onClose: () => void
}

export function DailyAttendancePopup(props: Props) {
    const {
        isShow,
        onClose,
        studentProfileIds = [],
        getListAttendances,
        selectedAttendance,
        isExternshipAttendance = false
    } = props
    const timezone = get(selectedAttendance, ["student", "timezone"], moment.tz.guess())
    const deleteEventIds = useRef<number[]>([])
    const {t} = useTranslation(["studentServices"])
    const [listEvent, setListEvent] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [showPopover, setShowPopover] = useState({})
    const [isVerified, setIsVerified] = useState(false)
    const [isMakeup, setIsMakeup] = useState(false)
    const [distEdEnabled, setDistEdEnabled] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isExcused, setIsExcused] = useState(false)
    const [studentInfo, setStudentInfo] = useState<any>()
    const [dateData, setDateData] = useState({
        checkInTime: "",
        checkOutTime: "",
        checkInDate: ""
    })
    const [message, setMessage] = useState("")
    const [deletedBy, setDeletedBy] = useState("")
    const [isLocked, setIsLocked] = useState(false)
    const [geoLocation, setGeoLocation] = useState<GeoLocationInfo | null>(null)
    const model = useModel()
    const timeFormat = model.getUserTimeFormat()
    const dateFormat = model.getUserDateFormat()
    const userGeolocationMapPopup = useVisible()
    const [isMapDisplayedAtLeastOnce, setIsMapDisplayedAtLeastOnce] = useState(false)
    const confirmDeletePopup = useVisible()

    const [originalListEvent, setOriginalListEvent] = useState<any[]>([])
    const [originalState, setOriginalState] = useState<any>({})
    const [eventErrors, setEventErrors] = useState<Map<string | number, string[]>>(new Map())

    const avatarUrl = studentInfo?.photo ? getUserPhotoUrl(studentInfo?.photo, 512) : "/image/DefaultAvatar.png"
    const {isBreakEnabled} = useAttendanceCheckBreakEventSetting()
    const {isEnabled: isAttendanceRoundingEnabled} = useAttendanceRounding({
        type: AttendanceRoundingType.Daily
    })

    const currentProfilePermissions = useCurrentProfilePermissions()
    const statusCanEditAttd = get(selectedAttendance, ["student", "status", "allowsDailyAttendanceManagement"], true)
    const {canEdit, canAdd, canDelete} = getDailyAttendancePermissions({
        isLocked: selectedAttendance?.isLocked,
        deletedAt: selectedAttendance?.deletedAt,
        allowsDailyAttendanceManagement: statusCanEditAttd,
        currentProfilePermissions
    })
    const {isModuleEnable} = useEdularModulesContext()

    const deleteDailyAttendanceMutation = useMutation(attendanceService.deleteDailyAttendance, {
        onSuccess: () => {
            toastSuccess("Deleted successfully")
            getListAttendances()
            onClose()
        },
        onError: (err) => {
            handleError(err, true)
        }
    })

    const loading = isSubmitting || deleteDailyAttendanceMutation.isLoading

    const getStudentInfo = useCallback(async () => {
        const profileId = studentProfileIds[0]
        const {
            data: [user]
        } = await userServiceV3.getAll({
            filter: {profileId: [profileId]},
            linkedObjects: true
        })
        setStudentInfo(user)
    }, [studentProfileIds])

    const getAttendanceDetail = useCallback(async () => {
        setIsLoading(true)
        try {
            const params = {
                attendanceIds: [selectedAttendance.id],
                includeAttendanceDeletedEvents: model.isStaffOrAdmin(),
                includeDeletedAttendance: model.isStaffOrAdmin()
            }
            let data: any = []
            if (isExternshipAttendance) {
                data = [selectedAttendance]
            } else {
                const {data: attendanceData} = await attendanceService.getDailyAttendanceList({
                    range: {
                        page: 1,
                        pageSize: 1
                    },
                    ...params
                })
                data = attendanceData
            }
            if (data?.length) {
                const attendance = get(data, [0])
                const events = (attendance.events ?? []).sort(function (left, right) {
                    return moment.utc(left.createdAt).diff(moment.utc(right.createdAt))
                })
                setMessage(attendance?.message ?? "")
                setIsLocked(attendance?.isLocked || attendance?.deletedAt)
                if (attendance?.deletedAt) {
                    setDeletedBy(attendance.deletedBy)
                }
                const checkinEvent = events.find(
                    (item) => item.eventType === AttendanceEventType.Checkin && !item.deletedAt
                )
                const checkoutEvent = events.find(
                    (item) => item.eventType === AttendanceEventType.Checkout && !item.deletedAt
                )
                setDateData({
                    ...dateData,
                    checkInTime: checkinEvent
                        ? moment(convertTZ(new Date(checkinEvent.createdAt), timezone)).format(timeFormat)
                        : "",
                    checkOutTime: checkoutEvent
                        ? moment(convertTZ(new Date(checkoutEvent.createdAt), timezone)).format(timeFormat)
                        : "",
                    checkInDate: checkinEvent
                        ? moment(convertTZ(new Date(checkinEvent.createdAt), timezone)).format(dateFormat)
                        : ""
                })
                setListEvent(
                    events.map((event) => {
                        return {
                            ...event,
                            createdAt: moment(event.createdAt).tz(timezone)
                        }
                    })
                )
                setOriginalListEvent(
                    cloneDeep(
                        events.map((event) => {
                            return {
                                ...event,
                                createdAt: moment(event.createdAt).tz(timezone)
                            }
                        })
                    )
                )
            }
        } catch (err) {
            handleError(err)
        } finally {
            setIsLoading(false)
        }
    }, [studentProfileIds, selectedAttendance, timezone, isExternshipAttendance, model])

    useEffect(() => {
        studentProfileIds.length && getStudentInfo()
        if (isShow && selectedAttendance && studentProfileIds?.length && selectedAttendance?.attendanceId) {
            getAttendanceDetail()
            const isMakeup = !!selectedAttendance.isMakeup
            const distEdEnabled = !!selectedAttendance.distEdEnabled
            const status = selectedAttendance.status
            let isVerified = selectedAttendance.isVerified === YesNo.Yes
            if (status === AttendanceStatus.Approved && isVerified) {
                isVerified = true
            } else if (status === AttendanceStatus.Rejected && isVerified) {
                isVerified = false
            } else {
                isVerified = null
            }
            setIsVerified(isVerified)
            setIsMakeup(isMakeup)
            setDistEdEnabled(distEdEnabled)
            setIsExcused(isExcused)
            setOriginalState({
                isVerified,
                isMakeup,
                distEdEnabled,
                isExcused,
                message: selectedAttendance?.message ?? ""
            })
        }
    }, [isShow, studentProfileIds, selectedAttendance])

    useEffect(() => {
        if (!isShow) {
            setStudentInfo(undefined)
            setListEvent([])
            deleteEventIds.current = []
            setOriginalListEvent([])
            setOriginalState({})
        }
    }, [isShow])

    useEffect(() => {
        if (userGeolocationMapPopup.isVisible && !isMapDisplayedAtLeastOnce) {
            setIsMapDisplayedAtLeastOnce(true)
        }
    }, [userGeolocationMapPopup.isVisible, isMapDisplayedAtLeastOnce])

    useEffect(() => {
        if (eventErrors.size === 0) return
        const allErrors = validateAttendanceEvents(listEvent)
        const newMap = buildEventErrorMap(allErrors)
        if (JSON.stringify([...newMap].sort()) !== JSON.stringify([...eventErrors].sort())) {
            setEventErrors(newMap)
        }
    }, [listEvent])

    const renderStudentInfo = () => {
        const profileId = studentProfileIds[0]
        const profile = (studentInfo?.profiles || []).find((item) => item.id === profileId)
        const phases: any[] = uniqBy(profile?.phases ?? [], "phaseId")
        const shifts: any[] = uniqBy(profile?.shifts ?? [], "shiftId")
        const program = get(profile, "currentMajor.name") || EMPTY_VALUE
        const startingTerm = get(profile, "startingTerm.name") || EMPTY_VALUE
        const currentTerm = get(profile, "currentTerm.name") || EMPTY_VALUE
        const fields = [
            {label: t("popup.userId"), value: studentInfo?.customUserId || EMPTY_VALUE},
            {label: t("popup.enrollmentId"), value: profile?.customProfileId || EMPTY_VALUE},
            {
                label: getFieldLabel(model, Settings.GeneralLabel.Campus, t("popup.campus")),
                value: !isEmpty(profile?.campuses)
                    ? profile.campuses.map((campus) => campus.name).join(", ")
                    : EMPTY_VALUE
            },
            {
                label: getFieldLabel(model, Settings.GeneralLabel.Program, t("popup.program")),
                value: program
            },
            {
                label: t("popup.phase"),
                value: !isEmpty(phases) ? phases.map((phase) => phase.name).join(", ") : EMPTY_VALUE
            },
            {
                label: t("popup.shift"),
                value: !isEmpty(shifts) ? shifts.map((shift) => shift.name).join(", ") : EMPTY_VALUE
            },
            {
                label: getFieldLabel(model, Settings.GeneralLabel.StartTerm, t("popup.startingTerm")),
                value: startingTerm
            },
            {
                label: getFieldLabel(model, Settings.GeneralLabel.CurrentTerm, t("popup.currentTerm")),
                value: currentTerm
            }
        ]

        return (
            <div className={styles.studentWrapper}>
                <div className={styles.imgWrapper}>
                    <img src={avatarUrl} className={styles.avatar} alt="" />
                </div>
                <div className={styles.studentDetails}>
                    <p className={styles.studentName}>{studentInfo?.fullName}</p>
                    <div className={styles.studentMeta}>
                        {fields.map((field) => (
                            <div key={field.label} className={styles.metaItem} title={String(field.value)}>
                                <span className={styles.metaLabel}>{field.label}</span>
                                <span className={styles.metaValue}>{field.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    const renderTitle = (eventType: string) => {
        if (eventType === "check-out") {
            return t("popup.checkout")
        } else if (eventType === "check-in") {
            return t("popup.checkin")
        } else if (eventType === "break-start") {
            return "Out"
        } else if (eventType === "break-end") {
            return "In"
        } else if (eventType === "lessonStart") {
            return "Lesson Start"
        } else if (eventType === "lessonEnd") {
            return "Lesson End"
        } else if (eventType === "attendance-check") {
            return "In"
        } else {
            return "Select Type"
        }
    }

    const renderIconAttendance = (eventType: string, status: string) => {
        if (eventType === "check-out") {
            return (
                <Icon
                    icon="CHECKOUT_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.checkOutIcon}
                />
            )
        } else if (eventType === "check-in") {
            return (
                <Icon
                    icon="CHECKIN_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else if (eventType === "break-start") {
            return <Icon icon="ATTENDANCE_OUT" color={"#DF1642"} className={styles.rightIcon} />
        } else if (eventType === "break-end") {
            return <Icon icon="ATTENDANCE_IN" color={"#18A957"} className={styles.rightIcon} />
        } else if (eventType === "attendance-check") {
            return (
                <Icon
                    icon="ATTENDANCE_IN"
                    color={status === "success" ? "#18A957" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else {
            return <Icon icon="ARROW_DOWN" color={"grey"} className={styles.arrowIcon} />
        }
    }

    const onPressDeleteEvent = (event: AttendanceEvent) => {
        typeof event.id === "number" && deleteEventIds.current.push(event.id)
        const events = listEvent.filter((item) => item.id !== event.id)
        setListEvent([...events])
    }

    const onAddEvent = (e) => {
        let newListEvent = clone(listEvent)
        newListEvent.push({
            status: "success",
            id: uuid()
        })
        setListEvent(newListEvent)
    }

    const renderAddEvent = () => {
        const checkIn = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkin && item.status === "success" && !item.deletedAt
        )
        const checkOut = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkout && item.status === "success" && !item.deletedAt
        )
        const canShowAddEvent = isBreakEnabled || !checkIn || !checkOut

        if (!canShowAddEvent) {
            return null
        }

        return (
            <div className={styles.buttonAddEvent}>
                <BaseButton disabled={!canAdd} title="Add Event" onClick={onAddEvent} />
            </div>
        )
    }

    const renderPopoverSelectEvent = (event: AttendanceEvent) => {
        const checkIn = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkin && item.status === "success" && !item.deletedAt
        )
        const checkOut = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkout && item.status === "success" && !item.deletedAt
        )
        let options = cloneDeep(EVENT_OPTIONS)
        if (event?.eventType) {
            options = options.filter((item) => item.value !== event.eventType)
        }
        if (checkIn) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkin)
        }
        if (checkOut) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkout)
        }
        if (!isBreakEnabled) {
            options = options.filter(
                (item) => ![AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.value)
            )
        }

        if (isEmpty(options)) {
            return null
        }

        return (
            <div className={styles.selectContainer}>
                {options.map((option) => (
                    <div
                        onClick={() => {
                            event.eventType = option.value
                            setListEvent([...listEvent])
                            showPopover[event.id] = false
                            setShowPopover({...showPopover})
                        }}
                        className={cx(styles.popupContainer, styles.hoverBackgroundGrey)}>
                        {renderIconAttendance(option.value, "success")}
                        <span style={{width: 120}} className={styles.infoLabel}>
                            {renderTitle(option.value)}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    const renderCheckInItem = (data: any) => {
        const event = listEvent.find((item) => item.id === data.id)
        const isDeleted = !!data?.deletedAt
        const isRenderDelete = event?.status === "success" && canEdit && !isDeleted
        const geoLocation = event?.jsonData?.geoLocation
        const errors = eventErrors.get(event?.id)
        const hasError = !!errors?.length

        return (
            <div
                key={event?.id ?? uuid()}
                style={{
                    opacity: isDeleted ? 0.5 : 1,
                    pointerEvents: isDeleted ? "none" : "auto"
                }}>
                <div style={{display: "flex", flexDirection: "column"}}>
                    <div
                        className={styles.infoWrapper}
                        style={
                            hasError
                                ? {
                                      borderLeft: "3px solid var(--error-400-base)",
                                      paddingLeft: 8,
                                      backgroundColor: "rgba(255, 77, 79, 0.06)",
                                      marginBottom: 0
                                  }
                                : {}
                        }>
                        {event?.status === "success" ? (
                            <div style={{display: "flex", flexDirection: "column"}}>
                                <BaseTimePicker
                                    style={{width: 130, marginRight: 24}}
                                    value={
                                        data?.createdAt ? moment(convertTZ(new Date(data?.createdAt), timezone)) : ""
                                    }
                                    format={timeFormat}
                                    allowClear={false}
                                    disabled={!!event?.id && !canEdit}
                                    onChange={(date) => {
                                        event.createdAt = moment(date).tz(timezone, true)
                                        setListEvent([...listEvent])
                                    }}
                                    placeholder="Time"
                                />
                                {isAttendanceRoundingEnabled && data?.dateTimeRounded && (
                                    <span style={{fontSize: 12, color: "#8c8c8c", marginTop: 5}}>
                                        Rounded:{" "}
                                        {moment(convertTZ(new Date(data.dateTimeRounded), timezone)).format(timeFormat)}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div style={{backgroundColor: "transparent"}} className={styles.hourLabel} />
                        )}
                        {event?.status === "success" ? (
                            <>
                                <Popover
                                    placement="right"
                                    color="white"
                                    visible={showPopover[event.id]}
                                    arrowContent={null}
                                    onVisibleChange={(visible) => {
                                        showPopover[event.id] = visible
                                        setShowPopover({...showPopover})
                                    }}
                                    content={() => renderPopoverSelectEvent(event)}
                                    trigger={!!event?.id && !canEdit ? [] : "click"}>
                                    <div className={styles.popupContainer}>
                                        {renderIconAttendance(data?.eventType, data.status)}
                                        <div className={styles.eventName}>
                                            <span
                                                style={{
                                                    width: 80,
                                                    color: data?.eventType ? "black" : "rgba(0,0,0,0.7)"
                                                }}
                                                className={styles.infoLabel}>
                                                {renderTitle(data?.eventType)}
                                            </span>
                                            {typeof data?.id === "number" ? (
                                                <>
                                                    <div className={styles.eventInfo}>
                                                        <span className={styles.eventId}>ID: {data.id} </span>
                                                        {data.synced ? (
                                                            <div className={styles.syncedIcon}>
                                                                <Icon
                                                                    icon="APPROVE"
                                                                    size={12}
                                                                    color={"var(--success-400-base)"}
                                                                />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    {data.syncedAt || data.synced_at ? (
                                                        <div className={styles.eventInfo}>
                                                            <span className={styles.eventId}>
                                                                Synced:{" "}
                                                                {moment(
                                                                    convertTZ(
                                                                        new Date(data.syncedAt || data.synced_at),
                                                                        timezone
                                                                    )
                                                                ).format(`${dateFormat} ${timeFormat}`)}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                    {data.sisId?.length ? (
                                                        <div className={styles.eventInfo}>
                                                            <span className={styles.eventId}>ExID: {data.sisId} </span>
                                                        </div>
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                </Popover>
                                {isModuleEnable(Modules.AcademicLocationTrackingAttendance) &&
                                isModuleEnable(Modules.AcademicUseGeoFencing) ? (
                                    [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(
                                        event?.eventType
                                    ) ? (
                                        <Checkbox
                                            checked={event?.breakEventType === BreakEventType.Lunch}
                                            disabled={!!event?.id && !canEdit}
                                            onChange={(e) => {
                                                event.breakEventType =
                                                    event.breakEventType === BreakEventType.Lunch
                                                        ? BreakEventType.Break
                                                        : BreakEventType.Lunch

                                                setListEvent([...listEvent])
                                            }}>
                                            <span className={styles.infoLabel}>Is Lunch</span>
                                        </Checkbox>
                                    ) : (
                                        <div style={{width: 96.8}} />
                                    )
                                ) : null}
                            </>
                        ) : (
                            <div className={styles.eventName}>
                                <span style={{width: 80}} className={styles.infoLabel}>
                                    {renderTitle(data?.eventType)}
                                </span>

                                {typeof data?.id === "number" ? (
                                    <>
                                        <div className={styles.eventInfo}>
                                            <span className={styles.eventId}>ID: {data.id} </span>
                                            {data.synced ? (
                                                <div className={styles.syncedIcon}>
                                                    <Icon icon="APPROVE" size={12} color={"var(--success-400-base)"} />
                                                </div>
                                            ) : null}
                                        </div>
                                        {data.syncedAt || data.synced_at ? (
                                            <div className={styles.eventInfo}>
                                                <span className={styles.eventId}>
                                                    Synced:{" "}
                                                    {moment(
                                                        convertTZ(new Date(data.syncedAt || data.synced_at), timezone)
                                                    ).format(`${dateFormat} ${timeFormat}`)}
                                                </span>
                                            </div>
                                        ) : null}
                                        {data.sisId?.length ? (
                                            <div className={styles.eventInfo}>
                                                <span className={styles.eventId}>ExID: {data.sisId} </span>
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}
                            </div>
                        )}
                        <div className={styles.applyRoundingContainer}>
                            <Checkbox
                                checked={event?.applyRounding}
                                disabled={!!event?.id && !canEdit}
                                onChange={(e) => {
                                    event.applyRounding = !event.applyRounding
                                    setListEvent([...listEvent])
                                }}>
                                <span className={styles.infoLabel}>Apply Rounding</span>
                            </Checkbox>
                            {data?.createdByUserName?.length ? (
                                <span className={styles.eventId}>Created By: {data.createdByUserName} </span>
                            ) : null}
                            {data?.modifiedByUserName?.length ? (
                                <span className={styles.eventId}>Modified By: {data.modifiedByUserName} </span>
                            ) : null}
                            {isDeleted ? (
                                <span className={styles.eventId}>
                                    Deleted At: {moment(data.deletedAt).format(model.getUserDateTimeFormat())}{" "}
                                </span>
                            ) : null}
                        </div>
                        {event?.status === "success" ? (
                            <BaseInput
                                value={event?.message ?? ""}
                                onChange={(text) => {
                                    event.message = text
                                    setListEvent([...listEvent])
                                }}
                                disabled={!!event?.id && !canEdit}
                                placeholder={t("popup.notes")}
                                className={styles.inputWidth}
                            />
                        ) : (
                            <div className={styles.inputWidth} />
                        )}
                        {!!geoLocation && (
                            <div
                                onClick={() => {
                                    setGeoLocation(geoLocation)
                                    userGeolocationMapPopup.open()
                                }}>
                                <Icon icon="LOCATION" className={styles.deleteIcon} />
                            </div>
                        )}
                        {isRenderDelete ? (
                            <div onClick={() => onPressDeleteEvent(event)}>
                                <Icon color={"var(--error-400-base)"} icon="DELETE" className={styles.deleteIcon} />
                            </div>
                        ) : null}
                    </div>
                    {/* close .infoWrapper */}
                    {hasError && (
                        <div style={{marginTop: 4, display: "flex", flexDirection: "column", gap: 2}}>
                            {errors.map((err: string, idx: number) => (
                                <span
                                    key={idx}
                                    style={{color: "var(--error-400-base)", fontSize: 12, lineHeight: "18px"}}>
                                    • {err}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                {/* close column wrapper */}
            </div>
        )
    }

    const resetForm = useCallback(() => {
        getListAttendances?.()
        setStudentInfo(undefined)
        deleteEventIds.current = []
        setListEvent([])
        setOriginalListEvent([])
        setOriginalState({})
        onClose()
    }, [getListAttendances])

    const onConfirmDeleteAttendance = useCallback(() => {
        const attendanceId = selectedAttendance.attendanceId

        deleteDailyAttendanceMutation.mutate({
            attendanceIds: [attendanceId]
        })
        confirmDeletePopup.close()
    }, [selectedAttendance])

    const hasChanges = useMemo(() => {
        if (deleteEventIds.current?.length) return true

        if (isVerified !== originalState.isVerified) return true
        if (isMakeup !== originalState.isMakeup) return true
        if (distEdEnabled !== originalState.distEdEnabled) return true
        if (isExcused !== originalState.isExcused) return true
        if ((message ?? "") !== (originalState.message ?? "")) return true

        const currEvents = listEvent.filter((e: any) => !e.deletedAt)
        const origEvents = (originalListEvent ?? []).filter((e: any) => !e.deletedAt)
        if (currEvents.length !== origEvents.length) return true

        for (let j = 0; j < currEvents.length; j++) {
            const ce = currEvents[j]
            const oe = origEvents[j]
            if (!oe) return true
            if (ce.id !== oe.id) return true
            if (ce.eventType !== oe.eventType) return true
            if (ce.applyRounding !== oe.applyRounding) return true
            if ((ce.message ?? "") !== (oe.message ?? "")) return true
            if (ce.breakEventType !== oe.breakEventType) return true
            if (ce.createdAt && oe.createdAt) {
                if (moment(ce.createdAt).valueOf() !== moment(oe.createdAt).valueOf()) return true
            } else if (ce.createdAt !== oe.createdAt) {
                return true
            }
        }
        return false
    }, [listEvent, originalListEvent, originalState, isVerified, isMakeup, distEdEnabled, message, isExcused])

    const onSave = useCallback(async () => {
        if (isExternshipAttendance) {
            onSaveExternshipAttendance()
            return
        }

        const allErrors = validateAttendanceEvents(listEvent)
        if (allErrors.length) {
            setEventErrors(buildEventErrorMap(allErrors))
            toastError("Punch sequence or logic error.  Please review highlighted punch.")
            return
        }

        setEventErrors(new Map())

        let listEventAdd = listEvent.filter((item) => typeof item.id !== "number")

        if (listEventAdd?.length && listEventAdd.find((event) => !event.eventType)) {
            return toastWarning("You need to select the event type: Check-in/Check-out or In/Out for all events")
        }

        if (listEventAdd?.length && listEventAdd.find((event) => !event.createdAt)) {
            return toastWarning("You need to select the time for all events")
        }

        setIsSubmitting(true)
        try {
            let status = selectedAttendance.status

            if (isVerified === true) {
                status = AttendanceStatus.Approved
            } else if (isVerified === false) {
                status = AttendanceStatus.Rejected
            }

            const params: any = {
                attendanceId: selectedAttendance.attendanceId,
                status: status,
                message,
                isVerified: [AttendanceStatus.Approved, AttendanceStatus.Rejected].includes(status) ? true : undefined,
                isMakeup: !!isMakeup,
                distEdEnabled: !!distEdEnabled
            }
            if (params.status === "approved") {
                params.isExcusedAbsence = false
            } else {
                params.isExcusedAbsence = !!isExcused
            }
            listEventAdd = listEventAdd.map((item) => {
                const createdAt = moment(item.createdAt).tz(timezone, true)
                const startDate = moment(selectedAttendance?.date).tz(timezone, true)
                createdAt.set({
                    month: startDate.month(),
                    date: startDate.date(),
                    year: startDate.year()
                })
                return {
                    message: item.message ?? "",
                    applyRounding: !!item.applyRounding,
                    eventType: item.eventType,
                    status: "success",
                    attendanceId: selectedAttendance.attendanceId,
                    createdAt: createdAt.utc().toISOString(),
                    breakEventType: [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(
                        item.eventType
                    )
                        ? item.breakEventType
                        : null
                }
            })

            const events = listEvent
                .filter((item) => typeof item.id === "number" && !item.deletedAt)
                .map((item) => {
                    const createdAt = moment(item.createdAt).tz(timezone, true)
                    const startDate = moment(selectedAttendance?.date).tz(timezone, true)
                    createdAt.set({
                        month: startDate.month(),
                        date: startDate.date(),
                        year: startDate.year()
                    })
                    return {
                        id: item.id,
                        applyRounding: !!item.applyRounding,
                        message: item.message ?? "",
                        eventType: item.eventType,
                        createdAt: createdAt.utc().toISOString(),
                        breakEventType: [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(
                            item.eventType
                        )
                            ? item.breakEventType
                            : null
                    }
                })
            if (events?.length) {
                params.events = events
            }
            if (deleteEventIds.current?.length) {
                params.deleteEventIds = deleteEventIds.current
            }
            if (listEventAdd.length) {
                params.addEvents = listEventAdd
            }
            await attendanceService.updateDailyAttendance(params)
            resetForm()
            toastSuccess("Updated successfully")
        } catch (err) {
            handleError(err)
        } finally {
            setIsSubmitting(false)
        }
    }, [
        resetForm,
        isVerified,
        selectedAttendance,
        isExcused,
        listEvent,
        timezone,
        isExternshipAttendance,
        isMakeup,
        distEdEnabled,
        message
    ])

    const onSaveExternshipAttendance = useCallback(async () => {
        const allErrors = validateAttendanceEvents(listEvent)
        if (allErrors.length) {
            setEventErrors(buildEventErrorMap(allErrors))
            toastError("Punch sequence or logic error.  Please review highlighted punch.")
            return
        }

        setEventErrors(new Map())

        setIsSubmitting(true)
        try {
            const params: any = {
                attendanceId: selectedAttendance.attendanceId
            }
            let events = listEvent.map((item) => {
                const createdAt = moment(item.createdAt)
                const startDate = moment(convertTZ(new Date(selectedAttendance?.date), timezone))
                createdAt.set({
                    month: startDate.month(),
                    date: startDate.date(),
                    year: startDate.year()
                })
                return {
                    id: typeof item.id === "number" ? item.id : undefined,
                    message: item.message ?? null,
                    eventType: item.eventType,
                    attendanceType: item.attendanceType ?? AttendanceType.Manual,
                    status: item.status,
                    createdAt: createdAt.toISOString()
                }
            })
            let deletedEvents: any = []
            if (deleteEventIds.current?.length) {
                deletedEvents = deleteEventIds.current.map((x) => {
                    const deletedAt = moment()
                    const startDate = moment(convertTZ(new Date(selectedAttendance?.date), timezone))
                    deletedAt.set({
                        month: startDate.month(),
                        date: startDate.date(),
                        year: startDate.year()
                    })
                    return {
                        id: x,
                        deletedAt
                    }
                })
            }
            events = events.concat(deletedEvents)
            if (events?.length) {
                params.events = events
            }
            await attendanceService.updateExternshipDailyAttendance(params)
            resetForm()
            toastSuccess("Updated successfully")
        } catch (err) {
            handleError(err)
        } finally {
            setIsSubmitting(false)
        }
    }, [resetForm, isVerified, selectedAttendance, listEvent, timezone])

    const checkInEvent = listEvent.filter(
        (event) => event.eventType === AttendanceEventType.Checkin && event.status === "success" && !event.deletedAt
    )
    const checkOutEvent = listEvent.filter(
        (event) => event.eventType === AttendanceEventType.Checkout && event.status === "success" && !event.deletedAt
    )
    const otherEvents = listEvent.filter(
        (event) =>
            (event.eventType !== AttendanceEventType.Checkout && event.eventType !== AttendanceEventType.Checkin) ||
            !!event.deletedAt
    )

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="PERSON_FILL" width="70vw">
            <div className={styles.popupWrapper}>
                <p className={styles.title}>{t("attendance.title")}</p>
                {selectedAttendance?.date ? (
                    <div>
                        <div className={styles.timeWrapper}>
                            <span>{formatDateOnly(selectedAttendance?.date, dateFormat)}</span>
                        </div>
                    </div>
                ) : null}
                <hr className={styles.divisor} />
                {renderStudentInfo()}
                {selectedAttendance?.attendanceId ? (
                    <>
                        {checkInEvent.map((item) =>
                            renderCheckInItem({
                                status: "failure",
                                eventType: AttendanceEventType.Checkin,
                                ...item
                            })
                        )}
                        {otherEvents.map(renderCheckInItem)}
                        {checkOutEvent.map((item) =>
                            renderCheckInItem({
                                status: "failure",
                                eventType: AttendanceEventType.Checkout,
                                ...item
                            })
                        )}
                        {renderAddEvent()}

                        <div className={`${styles.flexContainer} ${styles.alignItems}`}>
                            <BaseInput
                                value={message}
                                onChange={setMessage}
                                disabled={!canEdit}
                                placeholder={t("popup.notes")}
                            />
                        </div>
                        <hr className={styles.attendanceDivider} />
                        <IrregularAttendanceBanner
                            attendanceId={selectedAttendance.attendanceId}
                            status={selectedAttendance.status}
                            isVerified={selectedAttendance.isVerified}
                            hasCheckedOut={!!checkOutEvent.length}
                            disabled={!canEdit}
                            onConfirmed={() => {
                                getListAttendances?.()
                                onClose()
                            }}
                        />

                        {!isExternshipAttendance && (
                            <>
                                <div
                                    style={{
                                        cursor: !canEdit ? "not-allowed" : "pointer",
                                        pointerEvents: !canEdit ? "none" : "auto",
                                        opacity: !canEdit ? 0.7 : 1
                                    }}
                                    className={styles.optionsWrap}>
                                    <div className={styles.approveAttendWrap}>
                                        <div className={styles.attendedBodyItem} onClick={() => setIsVerified(true)}>
                                            <span className={styles.checkLabel}>Approve</span>
                                            <div className={styles.unchecked}>
                                                <Icon
                                                    icon="APPROVE"
                                                    size={24}
                                                    color={isVerified === true ? "var(--success-400-base)" : "grey"}
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.attendedBodyItem} onClick={() => setIsVerified(false)}>
                                            <span className={styles.checkLabel}>Reject</span>

                                            <div className={styles.unchecked}>
                                                <Icon
                                                    icon="CROSS_CIRCLE"
                                                    size={24}
                                                    color={isVerified === false ? "var(--error-400-base)" : "grey"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.makeupDistEdWrap}>
                                        <div
                                            className={styles.approveAttendWrap}
                                            onClick={() => setDistEdEnabled(!distEdEnabled)}>
                                            <span className={styles.checkLabel}>DistEd</span>
                                            <div
                                                className={cx(styles.uncheckedMakeup, {
                                                    [styles.checked]: distEdEnabled
                                                })}>
                                                <Icon
                                                    icon="TICK"
                                                    className={styles.attendanceTickIcon}
                                                    color="#e5e5e5"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={styles.approveAttendWrap}
                                            onClick={() => setIsMakeup(!isMakeup)}>
                                            <span className={styles.checkLabel}>Makeup</span>
                                            <div
                                                className={cx(styles.uncheckedMakeup, {
                                                    [styles.checked]: isMakeup
                                                })}>
                                                <Icon
                                                    icon="TICK"
                                                    className={styles.attendanceTickIcon}
                                                    color="#e5e5e5"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {selectedAttendance?.deletedAt ? (
                            <div
                                className={styles.extraData}
                                style={{flexDirection: "column", alignItems: "flex-start"}}>
                                <span
                                    className={styles.eventId}
                                    style={{
                                        fontSize: 16,
                                        color: "red"
                                    }}>
                                    {selectedAttendance.deletedBy ? `Deleted By: ${selectedAttendance.deletedBy} ` : ""}
                                    at:{" "}
                                    {moment(convertTZ(new Date(selectedAttendance.deletedAt), timezone)).format(
                                        `${dateFormat} ${timeFormat}`
                                    )}
                                </span>
                            </div>
                        ) : null}

                        {selectedAttendance?.verifiedAt ||
                        selectedAttendance?.verified_at ||
                        selectedAttendance?.approvedAt ||
                        selectedAttendance?.approved_at ? (
                            <div
                                className={styles.extraData}
                                style={{flexDirection: "column", alignItems: "flex-start"}}>
                                {selectedAttendance?.verifiedAt || selectedAttendance?.verified_at ? (
                                    <span className={styles.eventId}>
                                        Verified At:{" "}
                                        {moment(
                                            convertTZ(
                                                new Date(
                                                    selectedAttendance.verifiedAt || selectedAttendance.verified_at
                                                ),
                                                timezone
                                            )
                                        ).format(`${dateFormat} ${timeFormat}`)}
                                    </span>
                                ) : null}
                                {selectedAttendance?.approvedAt || selectedAttendance?.approved_at ? (
                                    <span className={styles.eventId}>
                                        Approved At:{" "}
                                        {moment(
                                            convertTZ(
                                                new Date(
                                                    selectedAttendance.approvedAt || selectedAttendance.approved_at
                                                ),
                                                timezone
                                            )
                                        ).format(`${dateFormat} ${timeFormat}`)}
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                        {model.isStaffOrAdmin() ? (
                            <div className={styles.saveContainer}>
                                <SecondaryButton
                                    loading={loading}
                                    disabled={!canDelete}
                                    onClick={confirmDeletePopup.open}
                                    title={"Delete"}
                                />
                                <BaseButton
                                    loading={loading}
                                    onClick={onSave}
                                    disabled={!hasChanges}
                                    title={t("popup.save")}
                                />
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className={styles.emptyView}>
                        <span className={styles.textEmpty}>Attendance is empty</span>
                    </div>
                )}
                <BaseLoading isShow={isLoading} />
                <BasePopup
                    width="600px"
                    isShow={userGeolocationMapPopup.isVisible}
                    onClose={userGeolocationMapPopup.close}>
                    {isMapDisplayedAtLeastOnce && geoLocation ? (
                        <div style={{padding: 20}}>
                            <GeoLocationMaps geoLocation={geoLocation} forceUpdateLocation showSearch={false} />
                        </div>
                    ) : null}
                </BasePopup>

                <ConfirmPopup
                    isVisible={confirmDeletePopup.isVisible}
                    title={"Are you sure you want to delete this attendance?"}
                    onClose={confirmDeletePopup.close}
                    onConfirm={onConfirmDeleteAttendance}
                />
            </div>
        </BasePopup>
    )
}
